/** The feature table (docs/spec/03 §4): what each depth unlocks, and the seeded pick of three to five. */

import type { Rng } from '../../sim/rng';
import { amoebaNursery, butterflyPocket, fireflyLoop, magicWall } from './creatures';
import { clocks, expandCorridor, slimeShelf, vault } from './deep';
import type { Build, FeatureId } from './plan';
import { cache, room, shelf, wallLine } from './structures';

interface FeatureSpec {
  readonly id: FeatureId;
  /** The first depth it may appear at (Classic features). */
  readonly from: number;
  /** A Deep element: only with the `deep` option. */
  readonly deep: boolean;
  readonly weight: number;
  /** At most this many in one cave. */
  readonly max: number;
  /** Build order: structures, creatures, then what must keep clear of creatures, then loose items. */
  readonly phase: number;
  readonly build: Build;
}

const FEATURES: readonly FeatureSpec[] = [
  { id: 'room', from: 1, deep: false, weight: 4, max: 3, phase: 0, build: room },
  { id: 'wall', from: 1, deep: false, weight: 3, max: 3, phase: 0, build: wallLine },
  { id: 'shelf', from: 1, deep: false, weight: 3, max: 3, phase: 0, build: shelf },
  { id: 'cache', from: 1, deep: false, weight: 3, max: 2, phase: 0, build: cache },
  { id: 'firefly', from: 2, deep: false, weight: 4, max: 2, phase: 1, build: fireflyLoop },
  { id: 'butterfly', from: 3, deep: false, weight: 4, max: 3, phase: 1, build: butterflyPocket },
  { id: 'amoeba', from: 5, deep: false, weight: 3, max: 1, phase: 2, build: amoebaNursery },
  { id: 'magic', from: 6, deep: false, weight: 3, max: 1, phase: 2, build: magicWall },
  { id: 'expand', from: 1, deep: true, weight: 3, max: 2, phase: 0, build: expandCorridor },
  { id: 'vault', from: 1, deep: true, weight: 3, max: 2, phase: 0, build: vault },
  { id: 'slime', from: 1, deep: true, weight: 3, max: 2, phase: 1, build: slimeShelf },
  { id: 'clocks', from: 1, deep: true, weight: 2, max: 1, phase: 3, build: clocks },
];

export interface PickedFeature {
  readonly id: FeatureId;
  readonly build: Build;
}

/** Features worth naming a cave after; the plainer ones, in order, if a cave has none of these. */
const SPECIAL: readonly FeatureId[] = ['vault', 'slime', 'expand', 'magic', 'amoeba', 'butterfly', 'firefly', 'clocks'];
const PLAIN: readonly FeatureId[] = ['shelf', 'room', 'cache', 'wall'];

/** A feature that unlocked in the last few depths is shown off: its weight is tripled. */
function weightOf(f: FeatureSpec, depth: number): number {
  if (f.deep) return f.weight * 2;
  return f.from > 1 && depth - f.from < 3 ? f.weight * 3 : f.weight;
}

function pickOne(rng: Rng, options: readonly FeatureSpec[], depth: number): FeatureSpec | undefined {
  const total = options.reduce((n, f) => n + weightOf(f, depth), 0);
  let r = rng.int(total);
  for (const f of options) {
    r -= weightOf(f, depth);
    if (r < 0) return f;
  }
  return options[options.length - 1];
}

/**
 * Three to five features for this depth, in build order. A Deep cave always leads with a Deep
 * feature so it looks the part. (Bonus caves pick a theme instead: `pickBonus`.)
 */
export function pickFeatures(rng: Rng, depth: number, deep: boolean): PickedFeature[] {
  const pool = FEATURES.filter((f) => (f.deep ? deep : depth >= f.from));
  const count = Math.min(5, 3 + rng.int(2) + (depth >= 6 ? 1 : 0));
  const counts = new Map<FeatureId, number>();
  const picks: FeatureSpec[] = [];
  for (let i = 0; i < count; i++) {
    const open = pool.filter((f) => (counts.get(f.id) ?? 0) < f.max && (i > 0 || !deep || f.deep));
    const f = pickOne(rng, open, depth);
    if (!f) break;
    counts.set(f.id, (counts.get(f.id) ?? 0) + 1);
    picks.push(f);
  }
  return picks
    .map((f, i) => ({ f, i }))
    .sort((a, b) => a.f.phase - b.f.phase || a.i - b.i)
    .map(({ f }) => ({ id: f.id, build: f.build }));
}

/** The feature a cave is known by (it names the cave and writes its hint): one of its special ones at random. */
export function headline(rng: Rng, placed: readonly FeatureId[]): FeatureId | undefined {
  const special = SPECIAL.filter((id) => placed.includes(id));
  if (special.length > 0) return special[rng.int(special.length)];
  return PLAIN.find((id) => placed.includes(id));
}
